// src/utils/apiClient.ts
import { ReactNode, useEffect } from "react";
import axios, {
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from "axios";
import { useAuth, IAuthContext } from "../hooks/check-auth";

/**
 * Interface for the API request options
 * Extends AxiosRequestConfig for flexibility
 */
export interface IAPIRequestOptions extends AxiosRequestConfig {
  headers?: Record<string, string>;
  data?: any;
  isS3UploadRequest?: boolean;
  trialNumber?: number;
  maxTrial?: number
  hideLoader?: boolean
}

/**
 * Interface for the AxiosInterceptor component props
 */
export interface IAxiosInterceptorProps {
  children: ReactNode;
}

// --- Axios instance setup ---
const apiClient = axios.create({
  baseURL: "https://app.connect-africa.world",
  headers: { "ngrok-skip-browser-warning": "69420" },
  timeout: 50000,
});

// --- API request wrapper ---
const makeAPIRequest = (options: IAPIRequestOptions): AxiosPromise => {
  const finalHeaders = { ...options.headers };
  const finalTimeout = options.hasOwnProperty('timeout') ? options?.timeout : 50000;
  
  if (!(options.data instanceof FormData) && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
  }

  return apiClient({
    ...options,
    headers: finalHeaders,
    timeout: finalTimeout
  });
};

// --- Axios Interceptor component ---
const AxiosInterceptor = ({ children }: IAxiosInterceptorProps) => {
  const { setSessionExpired, onShowSnackbar, setIsLoading } = useAuth() as IAuthContext;

  useEffect(() => {
    let activeRequests = 0;

    const reqInterceptor = (config: InternalAxiosRequestConfig | any): InternalAxiosRequestConfig => {
      activeRequests++;
      
      if(config?.isS3UploadRequest || config?.hideLoader){
        setIsLoading(false);
      }else{
        setIsLoading(true);
      }
      return config;
    };

    const resInterceptor = (response: AxiosResponse) => {
      activeRequests--;
      if (activeRequests === 0) setIsLoading(false);
      return response;
    };

    const errInterceptor = (error: AxiosError) => {
      console.error(`[Response Error] ${error.config?.url}`, error.response?.data || error.message);
      activeRequests--;
      if (activeRequests === 0) setIsLoading(false);

      if (error.response?.status === 401) {
        setSessionExpired(true, (error.response.data as any)?.error || "Unauthorized access");
      } else if (error.response?.status === 500) {
        onShowSnackbar((error.response.data as any)?.error || "Server error", "error");
      }


      let originalRequest: any = Object.assign({}, error.config);
      if (originalRequest.isS3UploadRequest && originalRequest.trialNumber < originalRequest.maxTrial) {
        originalRequest.trialNumber = originalRequest.trialNumber + 1;
        originalRequest._retry = true;
        return axios(originalRequest);
      } else {
        return Promise.reject(error);
      }

      // return Promise.reject(error);
    };

    const requestInterceptor = apiClient.interceptors.request.use(reqInterceptor, errInterceptor);
    const responseInterceptor = apiClient.interceptors.response.use(resInterceptor, errInterceptor);

    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
      apiClient.interceptors.response.eject(responseInterceptor);
    };
  }, [children]);

  return children;
};

export interface APIErrorResponse {
  error: string;
  message?: string;
  statusCode?: number;
}

export type APIError = AxiosError<APIErrorResponse>;

export { makeAPIRequest, AxiosInterceptor };